import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  type ContentSearchParams,
  parseContentSearch,
  searchQuery,
} from '@/admin/contentSearch';
import { LANGUAGE_LABELS } from '@/admin/languages';
import { LessonHeader, TabHeading } from '@/admin/ui/ContentNav';
import { SearchFilters } from '@/admin/ui/ContentSearch';
import { PageTitle } from '@/admin/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '@/admin/ui/Pagination';
import { Table } from '@/admin/ui/Table';
import { NeedsNewVersion } from '@/admin/ui/Versions';
import { CreateTopicButton } from '@/admin/widgets/CreateTopicButton';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type Topic = components['schemas']['TopicAdminSummary'];
type TopicSearchItem = components['schemas']['TopicSearchItem'];

export const Route = createFileRoute('/admin/topics/')({
  validateSearch: (
    s: Record<string, unknown>,
  ): { lesson?: number } & ContentSearchParams => ({
    // With a lesson, its topics; without, a search across every lesson.
    lesson: s.lesson != null ? Number(s.lesson) : undefined,
    ...parseContentSearch(s),
  }),
  component: TopicsPage,
});

function TopicsPage() {
  const { lesson } = Route.useSearch();
  return lesson !== undefined ? (
    <LessonTopics lesson={lesson} />
  ) : (
    <TopicSearch />
  );
}

const columnHelper = createColumnHelper<Topic>();

const columns = [
  columnHelper.accessor('order', {
    header: () => i18n.t('admin.topics.col.order'),
  }),
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor('title', {
    header: () => i18n.t('admin.common.title'),
  }),
  columnHelper.accessor('status', {
    header: () => i18n.t('admin.common.status'),
  }),
];

function LessonTopics({ lesson }: { lesson: number }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  // Admin endpoint: all statuses (the student lesson_topics is gated to published).
  const { data, isLoading, error } = api.adminListTopics.useQuery({
    path: { lesson_id: lesson },
    query: { limit: LIST_PAGE_SIZE, offset: LIST_PAGE_SIZE * page },
  });
  const lessonQuery = api.adminGetLesson.useQuery({
    path: { lesson_id: lesson },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <Message>{t('admin.topics.loading')}</Message>;
  if (error) return <Message $error>{t('admin.topics.errorLoading')}</Message>;

  const pagination = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={LIST_PAGE_SIZE}
      shown={data?.data.length ?? 0}
      total={data?.count}
      onPage={setPage}
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      {lessonQuery.data && <LessonHeader lesson={lessonQuery.data} />}
      <TabHeading
        action={
          lessonQuery.data?.version_state === 'draft' && (
            <CreateTopicButton lessonId={lesson} />
          )
        }
      >
        {t('admin.nav.topics')}
      </TabHeading>
      {lessonQuery.data && lessonQuery.data.version_state !== 'draft' && (
        <NeedsNewVersion
          target={{
            courseId: lessonQuery.data.course_id,
            version: lessonQuery.data.course_version,
            item: { entity: 'lesson', lineageId: lessonQuery.data.lineage_id },
          }}
        >
          {t('admin.topics.lockedNotice')}
        </NeedsNewVersion>
      )}
      {pagination()}

      <Table
        table={table}
        to="/admin/topics/$topicId/"
        getParams={(row) => ({ topicId: String(row.id) })}
      />
      {pagination(true)}
    </div>
  );
}

const searchColumnHelper = createColumnHelper<TopicSearchItem>();

const searchColumns = [
  searchColumnHelper.accessor('id', {
    header: () => i18n.t('admin.common.id'),
  }),
  searchColumnHelper.accessor('title', {
    header: () => i18n.t('admin.common.title'),
  }),
  searchColumnHelper.accessor('lesson_title', {
    header: () => i18n.t('admin.topics.col.lesson'),
  }),
  searchColumnHelper.accessor('course_title', {
    header: () => i18n.t('admin.topics.col.course'),
  }),
  searchColumnHelper.accessor('language', {
    header: () => i18n.t('admin.common.language'),
    cell: (info) => i18n.t(LANGUAGE_LABELS[info.getValue()]),
  }),
  searchColumnHelper.accessor('status', {
    header: () => i18n.t('admin.common.status'),
  }),
];

function TopicSearch() {
  const { t } = useTranslation();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const page = (params.page ?? 1) - 1;

  const { data, isLoading, error } = api.adminSearchTopics.useQuery({
    query: searchQuery(params, LIST_PAGE_SIZE),
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns: searchColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const update = (patch: ContentSearchParams, replace = true) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace });

  const pagination = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={LIST_PAGE_SIZE}
      shown={data?.data.length ?? 0}
      total={data?.count}
      onPage={(next) =>
        update({ page: next > 0 ? next + 1 : undefined }, false)
      }
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      <PageTitle>{t('admin.nav.topics')}</PageTitle>
      <Intro>{t('admin.topics.intro')}</Intro>
      <SearchFilters
        params={params}
        onChange={update}
        placeholder={t('admin.topics.searchPlaceholder')}
      />
      {isLoading ? (
        <Message>{t('admin.topics.loading')}</Message>
      ) : error ? (
        <Message $error>{t('admin.topics.errorLoading')}</Message>
      ) : data?.count === 0 ? (
        <Message>{t('admin.topics.noMatch')}</Message>
      ) : (
        <>
          {pagination()}
          <Table
            table={table}
            to="/admin/topics/$topicId/"
            getParams={(row) => ({ topicId: String(row.id) })}
          />
          {pagination(true)}
        </>
      )}
    </div>
  );
}

const Intro = styled.p`
  color: var(--grey-700);
  font-size: 0.875rem;
  margin: 0.25rem 0 0;
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
